import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Row, Spinner, Table } from 'react-bootstrap';
import { CursosService } from '../../../../service/cursos.service';
import { ProgresoUsuariosResponse } from '../../../../service/types/cursos.types';

interface ProgresoUsuariosManagerProps {
  cursoId: string;
  cursoTitulo?: string;
}

export default function ProgresoUsuariosManager({ cursoId, cursoTitulo }: ProgresoUsuariosManagerProps) {
  const [progreso, setProgreso] = useState<ProgresoUsuariosResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [reiniciando, setReiniciando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [descargando, setDescargando] = useState<'todos' | 'completados' | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await CursosService.getProgresoUsuarios(cursoId);
      setProgreso(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setMensaje(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursoId]);

  const completados = progreso?.completados ?? [];
  const pendientes = progreso?.pendientes ?? [];

  const handleReiniciar = async () => {
    const confirmado = window.confirm(
      `¿Reiniciar este curso para los ${completados.length} usuario(s) que ya lo completaron? ` +
        'Volverán a figurar como pendientes y deberán rehacer todos los módulos y firmar de nuevo. ' +
        'El historial de completitudes previas no se pierde.',
    );
    if (!confirmado) return;

    setReiniciando(true);
    setMensaje(null);
    try {
      const { reiniciados } = await CursosService.reiniciarCurso(cursoId);
      setMensaje(`Se reinició el curso para ${reiniciados} usuario(s).`);
      await load();
    } catch {
      setMensaje('No se pudo reiniciar el curso. Intente nuevamente.');
    } finally {
      setReiniciando(false);
    }
  };

  const handleDescargarResultados = async (soloCompletados: boolean) => {
    const key = soloCompletados ? 'completados' : 'todos';
    setDescargando(key);
    try {
      const blob = await CursosService.descargarResultados(cursoId, soloCompletados);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resultados-${soloCompletados ? 'completados' : 'todos'}-${cursoTitulo ?? cursoId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setDescargando(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" /> Cargando progreso de usuarios...
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-end gap-2 mb-3">
        <Button variant="outline-primary" size="sm" onClick={() => handleDescargarResultados(false)} disabled={descargando !== null}>
          {descargando === 'todos' ? 'Generando...' : 'Descargar resultados (todos)'}
        </Button>
        <Button
          variant="outline-success"
          size="sm"
          onClick={() => handleDescargarResultados(true)}
          disabled={descargando !== null || completados.length === 0}
        >
          {descargando === 'completados' ? 'Generando...' : 'Descargar resultados (completados)'}
        </Button>
        <Button variant="outline-danger" size="sm" onClick={handleReiniciar} disabled={reiniciando || completados.length === 0}>
          {reiniciando ? 'Reiniciando...' : 'Reiniciar curso para todos los completados'}
        </Button>
      </div>
      {mensaje && (
        <Alert variant="info" onClose={() => setMensaje(null)} dismissible>
          {mensaje}
        </Alert>
      )}
      <Row>
        <Col md={6} className="mb-3">
          <Card className="shadow-sm h-100">
            <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
              <span>Completaron el curso</span>
              <Badge bg="light" text="dark">
                {completados.length}
              </Badge>
            </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Usuario</th>
                  <th>RUT</th>
                  <th>Completado el</th>
                  <th>Firma</th>
                </tr>
              </thead>
              <tbody>
                {completados.map((u) => (
                  <tr key={u.rut}>
                    <td>{u.name}</td>
                    <td>{u.rut}</td>
                    <td>{u.completadoEn ? new Date(u.completadoEn).toLocaleDateString() : '-'}</td>
                    <td>
                      {u.firma ? (
                        <img src={u.firma} alt="Firma" style={{ maxWidth: '150px', maxHeight: '80px' }} />
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
                {completados.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted">
                      Nadie ha completado este curso aún
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Col>
      <Col md={6} className="mb-3">
        <Card className="shadow-sm h-100">
          <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
            <span>Pendientes</span>
            <Badge bg="light" text="dark">
              {pendientes.length}
            </Badge>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Usuario</th>
                  <th>RUT</th>
                </tr>
              </thead>
              <tbody>
                {pendientes.map((u) => (
                  <tr key={u.rut}>
                    <td>{u.name}</td>
                    <td>{u.rut}</td>
                  </tr>
                ))}
                {pendientes.length === 0 && (
                  <tr>
                    <td colSpan={2} className="text-center text-muted">
                      No hay usuarios pendientes
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Col>
      </Row>
    </>
  );
}
